import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setIsMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Collection', path: '/collection' },
    { label: 'New Drops', path: '/new-drops' },
    { label: 'Journal', path: '/journal' },
    { label: 'Learn', path: '/learn' },
    { label: 'About', path: '/about' },
  ];

  const moreLinks = [
    { label: 'About Us', path: '/about-us' },
    { label: 'The Lab', path: '/lab' },
    { label: 'Ritual Guide', path: '/rituals' },
    { label: 'Membership', path: '/membership' },
    { label: 'Rewards', path: '/rewards' },
    { label: 'Gift a Secret', path: '/gift' },
    { label: 'Shipping', path: '/shipping' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  const allLinks = [...navLinks, ...moreLinks];

  const isActive = (path) => {
    if (path === '/collection') {
      return location.pathname === '/collection' || location.pathname.startsWith('/collection/') || location.pathname.startsWith('/product/');
    }
    return location.pathname === path;
  };

  const isMoreActive = moreLinks.some((link) => isActive(link.path));

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#050505]/90 backdrop-blur-lg border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button
              data-testid="header-logo"
              onClick={() => navigate('/collection')}
              className="font-serif text-xl md:text-2xl text-zinc-50 hover:opacity-80 transition-opacity"
            >
              Secret 13
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => navigate(link.path)}
                  className={`font-sans text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    isActive(link.path) ? 'text-white' : 'text-zinc-500 hover:text-white'
                  }`}
                >
                  {link.label}
                </button>
              ))}

              {/* More Dropdown */}
              <div ref={moreRef} className="relative">
                <button
                  data-testid="nav-more"
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  className={`flex items-center gap-1 font-sans text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    isMoreActive || isMoreOpen ? 'text-white' : 'text-zinc-500 hover:text-white'
                  }`}
                >
                  More
                  <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isMoreOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isMoreOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-3 w-48 bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 py-2"
                    >
                      {moreLinks.map((link) => (
                        <button
                          key={link.label}
                          data-testid={`nav-more-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                          onClick={() => {
                            navigate(link.path);
                            setIsMoreOpen(false);
                          }}
                          className={`w-full text-left px-5 py-2.5 font-sans text-xs tracking-[0.1em] transition-colors duration-200 ${
                            isActive(link.path) ? 'text-white bg-white/5' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {link.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-5">
              {/* Profile */}
              <button
                data-testid="nav-profile"
                onClick={() => navigate('/profile')}
                className="hidden sm:flex text-zinc-400 hover:text-white transition-colors"
              >
                <User className="w-4 h-4" />
              </button>

              {/* Cart */}
              <button
                data-testid="collection-button"
                onClick={() => navigate('/checkout')}
                className="relative flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <span className="hidden sm:inline font-sans text-xs tracking-[0.15em] uppercase">
                  Your Collection
                </span>
                {itemCount > 0 && (
                  <span className="w-5 h-5 bg-white text-black text-[10px] font-medium flex items-center justify-center rounded-full">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-zinc-400 hover:text-white transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-lg md:hidden overflow-y-auto"
          >
            <div className="flex flex-col items-center pt-28 pb-12 gap-6 min-h-screen">
              {allLinks.map((link, index) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    navigate(link.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`font-serif text-2xl transition-colors ${
                    isActive(link.path) ? 'text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {link.label}
                </motion.button>
              ))}

              <div className="w-8 h-px bg-zinc-800 my-2" />

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: allLinks.length * 0.05 }}
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className="font-serif text-2xl text-zinc-400 hover:text-white transition-colors"
              >
                Profile
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: (allLinks.length + 1) * 0.05 }}
                onClick={() => {
                  navigate('/checkout');
                  setIsMobileMenuOpen(false);
                }}
                className="font-serif text-2xl text-zinc-400 hover:text-white transition-colors flex items-center gap-4"
              >
                Your Collection
                {itemCount > 0 && (
                  <span className="w-6 h-6 bg-white text-black text-sm font-medium flex items-center justify-center rounded-full">
                    {itemCount}
                  </span>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
